import { useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError } from "./api";
import { fmtNum, fmtPct } from "./format";
import { Chat } from "./components/Chat";
import { Diagnostic } from "./components/Diagnostic";
import { EvalWorkbench } from "./components/EvalDashboard";
import { GlossaryPage } from "./components/GlossaryPage";
import { People } from "./components/People";
import { Access } from "./components/Access";
import { writeQuery } from "./urlState";
import { Districts } from "./components/Districts";
import { ContentProviders, PlatformScreen, SchoolsScreen } from "./components/Organizations";
import { ContentCurricula, ContentRubrics, ContentTasks, ContentTraits } from "./components/Content";
import { Roles } from "./components/Roles";
import { ScoringExperiments, ScoringFitGates, ScoringModels, ScoringRaters, ScoringRubrics,
         ScoringScorers, ScoringSets }
  from "./components/Scoring";
import { GlossaryProvider } from "./components/Term";
import { ScoreRunProvider } from "./components/ScoreRunContext";
import { FolderConfirm } from "./components/FolderConfirm";
import { ReviewConsole } from "./components/ReviewConsole";
import { NeedsStrip } from "./components/NeedsStrip";
import { PlanView } from "./components/PlanView";
import { DriveConnection } from "./components/DriveConnection";
import { Incoming } from "./components/Incoming";
import { ScoreRun } from "./components/ScoreRun";
import { applyChatWorkspace, defaultSpecForLevel } from "./workspace";
import {
  offered, ORDER, pathFor, placeFromPath, PRODUCTS, requestedTab, SECTION_LABEL, sectionsFor, tabFor,
  navTabsFor,
  type Place, type Product, type Section,
} from "./products";
import {
  chooseActivity, rememberActivity, scopeOf, type Activity, type ActivitiesResponse,
  type District as OwnDistrict,
} from "./activities";
import { Shell } from "./shell/Shell";
import { landingHref } from "./shell/PlanNav";
import { folderLabel } from "./home";
import type { HomeView } from "./home";
import {
  AdminOnlyState, NoChatState, NotFoundState, PageSkeleton, RefusedState,
} from "./shell/states";
import "./shell/shell.css";
import {
  byRecency, createSession, forkSession, loadStore, reconcileSchoolChange, relTime, saveStore,
  titleFor, upsert,
} from "./sessions";
import type { Session } from "./sessions";
import type {
  ChatTurn, ChatWorkspace, District, DiagnosticSchool, Level, Peer,
  WorkspaceData, WorkspaceSpec,
} from "./types";

// The screen shows one product's one section at a time. Which products and tabs exist, and what
// their URLs are, lives in ./products — this file only follows it.
//
// The last product used is remembered on this device, so `/` reopens where you were. Device-local
// convenience only: nothing here decides what anyone may see.
const PRODUCT_KEY = "sip.product";

/** The collection in the plan that matches an activity, so its folder can be shown beside the
 *  button that reads it. The plan already carries it; a second request would ask what we know. */
function folderOf(plan: HomeView | null, activityId: string) {
  for (const c of plan?.courses ?? []) {
    for (const u of c.units) {
      for (const l of u.lessons) {
        const w = l.work.find((x) => x.activity_id === activityId);
        if (w) return w;
      }
    }
  }
  return null;
}

/** The plan with one collection's folder replaced by what the server just confirmed. */
function withFolder(plan: HomeView | null, activityId: string,
                    f: { url: string | null; name: string | null }): HomeView | null {
  if (!plan) return plan;
  return {
    ...plan,
    courses: plan.courses.map((c) => ({
      ...c,
      units: c.units.map((u) => ({
        ...u,
        lessons: u.lessons.map((l) => ({
          ...l,
          work: l.work.map((w) => w.activity_id === activityId
            ? { ...w, folder_url: f.url, folder_name: f.name } : w),
        })),
      })),
    })),
  };
}

/** The plan for a year, or for whatever the server calls today when nobody has chosen. */
function yearQuery(year: number | null): string {
  return year === null ? "/cohorts/home" : `/cohorts/home?school_year=${year}`;
}

function rememberedProduct(available: Product[]): Product {
  let saved: string | null = null;
  try { saved = localStorage.getItem(PRODUCT_KEY); } catch { /* storage blocked: fall through */ }
  return available.find((p) => p === saved) ?? available[0] ?? "sip";
}

function rememberProduct(p: Product) {
  try { localStorage.setItem(PRODUCT_KEY, p); } catch { /* storage blocked: harmless */ }
}

const DEMO_DISTRICT = "0622500"; // Long Beach Unified (NCES LEAID) — the demo default
const LEVELS: Level[] = ["High", "Middle", "Primary"];

// Loaded once at import: the persisted sessions seed the initial picker/workspace state,
// so a returning user opens on what they were last looking at.
const BOOT = loadStore();
const BOOT_ACTIVE = BOOT.sessions.find((s) => s.id === BOOT.active_id) ?? null;

// Header stat. `null` renders as an em dash, never 0 — a missing demographic is unknown, not zero.
function Feat({ v, kind, lab }: { v: number | string | null; kind: "pct" | "num" | "str"; lab: string }) {
  const shown = v == null ? "—" : kind === "pct" ? fmtPct(v as number) : kind === "num" ? fmtNum(v as number) : v;
  return (
    <div>
      <b>{shown}</b> <span>{lab}</span>
    </div>
  );
}

type LoadState = "loading" | "ready" | "error";

// The selected school carries its own header demographics, alongside the diagnostic fields.
interface SchoolRow extends DiagnosticSchool {
  enroll_total: number | null;
  pct_sed: number | null;
  pct_el: number | null;
  pct_swd: number | null;
  locale: string | null;
}

export default function App() {
  const [level, setLevel] = useState<Level>(BOOT_ACTIVE?.level ?? "High");
  const [districtId, setDistrictId] = useState(BOOT_ACTIVE?.district_id ?? DEMO_DISTRICT);
  const [districts, setDistricts] = useState<District[]>([]);
  const [schools, setSchools] = useState<SchoolRow[]>([]);
  const [sel, setSel] = useState<SchoolRow | null>(null);
  const [peers, setPeers] = useState<Peer[] | null>(null);
  const [loadState, setLoadState] = useState<LoadState>("loading");

  // Sessions: a session pins ONE school + the workspace spec + the transcript. The ACTIVE
  // session is the source of truth the rest of this state is a view of (design § Sessions).
  const [sessions, setSessions] = useState<Session[]>(BOOT.sessions);
  const [activeId, setActiveId] = useState<string | null>(BOOT.active_id);
  // Where you are: a product and one of its tabs, mirrored in the URL (/writing/review).
  // `wantedPath` is the URL you asked for. It is resolved again when the server says which
  // products you have and whether you are an operator, so a deep link to an operator's tab
  // survives the moment before /me answers instead of being downgraded and forgotten.
  const [isAdmin, setIsAdmin] = useState(false);
  // Configuration → Access, by person: whose access is open, when a person was chosen.
  // Whether /me has answered. Until it has, "not an operator" is a guess, and showing "this
  // page is for operators" on a guess would flash it at every operator on every deep link.
  const [adminKnown, setAdminKnown] = useState(false);
  // Chat is a capability someone is given (0089); unknown until /me answers.
  const [mayChat, setMayChat] = useState<boolean | null>(null);
  // Until /me answers: every product that has a screen, never one that has none.
  const [products, setProducts] = useState<Product[]>(() => offered(ORDER));
  const wantedPath = useRef(window.location.pathname);
  const [place, setPlace] = useState<Place>(() => {
    const available = offered(ORDER);
    return placeFromPath(wantedPath.current, available, false, rememberedProduct(available));
  });
  const { product, section } = place;

  // Student Learning's learner activities: null until /api/activities answers. The database
  // scopes them to the caller's own classes (writing/serving/activities.py).
  const [activities, setActivities] = useState<Activity[] | null>(null);
  const [activitiesAvailable, setActivitiesAvailable] = useState(true);
  // The status /api/activities failed with, if it did, and the server's sentence. A 403 here is
  // sign-in refused after all (disabled since the page loaded), the answer for the whole product.
  // Reaching no class is not an error: it is an empty list.
  const [activitiesError, setActivitiesError] = useState<number | null>(null);
  const [refusedDetail, setRefusedDetail] = useState<string | null>(null);
  // Whose school system this is. Shown in the chrome for Student Learning, where the district
  // is the caller's own; School Improvement browses many districts, so it shows none.
  const [district, setDistrict] = useState<OwnDistrict | null>(null);
  // The curriculum plan and the cross-class queue. The sidebar IS the plan (shell/PlanNav.tsx),
  // so this is nav data, not a screen's data — which is why it is fetched here and not in a page.
  const [plan, setPlan] = useState<HomeView | null>(null);
  // Bumped when work is taken, to remount the console and re-read the plan: papers that just
  // arrived are not on screen until something asks again.
  const [reload, setReload] = useState(0);
  // Which school year the plan is for. Null means "whatever the server thinks today is" — the
  // teacher has not chosen, so the app should not pretend they did.
  const [year, setYear] = useState<number | null>(null);
  // A link naming an activity this teacher can't see. The database hides other classes, so to this
  // caller it doesn't exist: a 404, not a quiet switch to their own class under the same URL shape.
  const askedMissing = product === "writing" && !!place.activityId && !!activities
    && activitiesAvailable && !activities.some((a) => a.activity_id === place.activityId);
  const activity = product === "writing" && activities && !askedMissing
    ? chooseActivity(activities, place.activityId) : null;

  // The Claude-controlled workspace: `wspec` is what should be on screen (the active
  // session's spec), `ws` is the server-built data for it.
  const [wspec, setWspec] = useState<WorkspaceSpec>(
    BOOT_ACTIVE?.workspace ?? defaultSpecForLevel(BOOT_ACTIVE?.level ?? "High"),
  );
  const [ws, setWs] = useState<WorkspaceData | null>(null);

  // Server-owned level-aware default specs (GET /marts/workspace-defaults), fetched once.
  // `resolveDefault(level)` prefers these; defaultSpecForLevel falls back to the client map
  // until they load. A new/repointed session for an ES/MS school seeds CAASPP outcomes, not
  // the HS-only grad/college that would render as errors.
  const wsDefaultsRef = useRef<Record<string, WorkspaceSpec> | null>(null);
  const resolveDefault = (lvl: Level) => defaultSpecForLevel(lvl, wsDefaultsRef.current);

  // Refs read by effects/handlers without re-triggering them. `wspecRef` is also written
  // DIRECTLY on session adoption so the sibling fetch effect (same commit) sees the adopted
  // spec, not last render's.
  const wspecRef = useRef(wspec);
  wspecRef.current = wspec;
  const sessionsRef = useRef(sessions);
  sessionsRef.current = sessions;
  const activeRef = useRef(activeId);
  activeRef.current = activeId;
  // A school switch that needs a roster (district/level) reload first parks its target here.
  const pendingSelRef = useRef<string | null>(BOOT_ACTIVE?.school_id ?? null);
  // A chat set_school forks the conversation into the new school's session (transcript
  // copies forward, workspace resets). Set before the selection changes; consumed once.
  const forkFromRef = useRef<Session | null>(null);

  const activeSession = useMemo(
    () => sessions.find((s) => s.id === activeId) ?? null,
    [sessions, activeId],
  );

  // Persist on every mutation (saveStore debounces).
  useEffect(() => {
    saveStore({ v: 1, active_id: activeId, sessions });
  }, [sessions, activeId]);

  useEffect(() => {
    api
      .get<{ products?: string[]; operator?: boolean; chat?: boolean }>("/me")
      .then((d) => {
        const p = offered(d.products);
        if (p.length) setProducts(p);
        // Preva's operators see the evaluation tools; nobody else does (the Access design).
        setIsAdmin(!!d.operator);
        setMayChat(!!d.chat);
      })
      .catch(() => {})
      .finally(() => setAdminKnown(true));
    // Recorded on sign-in (docs/ADMIN.md step 4a): the server writes the caller's own record from
    // their verified token. Fire and forget — a failure here must never stop anyone working.
    api.post("/people/me", {}).catch(() => {});
    api
      .get<{ districts: District[] }>("/marts/districts")
      .then((d) => setDistricts(d.districts || []))
      .catch(() => {});
    // The server-owned level-aware defaults (one home for the per-level curation).
    api
      .get<Record<string, WorkspaceSpec>>("/marts/workspace-defaults")
      .then((d) => { wsDefaultsRef.current = d; })
      .catch(() => {}); // client fallback covers it
  }, []);

  // Re-resolve the requested URL whenever what you may see changes, and on back/forward. Losing
  // admin drops you out of an admin tab only, into the same product's first tab — never out of a
  // teacher's own screen.
  useEffect(() => {
    const resolve = () =>
      setPlace(placeFromPath(wantedPath.current, products, isAdmin, rememberedProduct(products)));
    resolve();
    const onPop = () => { wantedPath.current = window.location.pathname; resolve(); };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [products, isAdmin]);

  // `{Tab} — {Product}`, per route (docs/DESIGN.md, "Links and the URL").
  useEffect(() => {
    document.title = `${SECTION_LABEL[section]} — ${PRODUCTS[product].label}`;
  }, [product, section]);

  useEffect(() => {
    if (!products.includes("writing")) return;
    api
      .get<ActivitiesResponse>("/activities")
      .then((d) => {
        setActivitiesAvailable(d.available);
        setActivities(d.activities || []);
        setDistrict(d.district ?? null);
      })
      .catch((e) => {
        setActivitiesError(e instanceof ApiError ? e.status : 0);
        setRefusedDetail(e instanceof ApiError ? e.detail ?? null : null);
        setActivitiesAvailable(false);
        setActivities([]);
      });
    // The plan, which is the sidebar. Its failure is not the product's failure: a refused sign-in
    // is already reported through /activities above, and a teacher whose plan cannot be
    // read can still open and review student work. The nav degrades; the work does not.
    api.get<HomeView>(yearQuery(year))
      .then(setPlan)
      .catch(() => setPlan({ available: false, needs: [], courses: [],
                             school_year: null, years: [] }));
  }, [products, year]);

  // WHAT IS OPEN, which is a lesson page as often as a folder. Computed from the activity alone,
  // this left the last folder lit in the tree while a lesson was on screen — and the lesson
  // itself unmarked, so the nav pointed at the wrong row.
  const navHref = place.section === "plan" && place.planId
    // A UNIT page is a place too. Only the lesson case was handled, so opening a unit left the
    // last folder lit and the unit itself unmarked — the same defect one level up.
    ? `/writing/${place.planKind === "unit" ? "u" : "l"}/${encodeURIComponent(place.planId)}`
    : activity ? `/writing/a/${activity.activity_id}/review` : null;

  // One way to re-read the plan. Called when something CHANGES it — work taken in, a collection
  // made — because the plan on screen is then out of date in a way nothing else will notice. The
  // server's answer replaces it rather than the client patching a row in: a patched row shows what
  // we assumed was written, which is the wrong thing to show if anything about the write differed.
  function refreshPlan() {
    api.get<HomeView>(yearQuery(year)).then(setPlan).catch(() => {});
  }

  // One way to go somewhere by href, so the strip, the plan tree and the plan view cannot drift
  // into three slightly different navigations.
  function goTo(href: string) {
    window.history.pushState(null, "", href);
    wantedPath.current = href;
    setPlace(placeFromPath(href, products, isAdmin, product));
  }

  // Land on the lesson the class is actually on. There is no home screen to arrive at any more:
  // the plan is the nav, and the first thing a teacher wants is the work in front of them.
  // `replaceState`, not push — arriving somewhere is not a step to go back from.
  useEffect(() => {
    // Not when a URL asked for a screen that is not a collection: landing is for arriving with
    // nowhere in mind. This was `place.section === "plan"`, named one at a time, and the comment
    // beside it recorded what that costs — "the page existed and could not be reached". Adding
    // Glossary reproduced it exactly. So the rule is the one that was meant: a PRODUCT-scoped
    // section is a place you asked for, and the next tab does not have to rediscover this.
    // AND NOT BEFORE /admin/status HAS ANSWERED. `placeFromPath` filters sections by `isAdmin`, so
    // until the flag arrives an admin-only section parsed from a URL is downgraded to the product's
    // first tab. The re-resolve above is built to recover from exactly that — but only if the URL
    // asked for still says so, and landing REPLACES it. So a deep link to an admin screen was lost
    // in the gap, which is what `adminKnown` exists to stop being guessed at.
    // `place.section === "plan"` STAYS. A unit or lesson URL is /writing/u/<id>, whose second
    // segment is "u", not a section — so the path check below does not see it, and dropping this
    // would put back the very bug the comment above remembers.
    if (product !== "writing" || place.activityId || place.section === "plan"
        || !adminKnown || !plan?.courses?.length) return;
    // READ FROM THE URL THAT WAS ASKED FOR, not from the resolved section. `place.section` is
    // whatever survived `placeFromPath`, and for an admin-only screen that is the product's first
    // tab until /admin/status answers — so testing it made this a RACE between this effect and the
    // re-resolve, which it won sometimes and lost sometimes. `tabFor` does not filter by admin, so
    // asking the path directly is the same answer at every moment.
    const asked = wantedPath.current.split("/").filter(Boolean);
    if (asked[0] === product && asked[1]
        && tabFor(product, asked[1] as Section)?.scope === "product") return;
    for (const c of plan.courses) {
      // The COLLECTION, not the lesson: a lesson with a draft and a final has no page of its own.
      const href = landingHref(c);
      if (href) {
        window.history.replaceState(null, "", href);
        wantedPath.current = href;
        setPlace(placeFromPath(href, products, isAdmin, product));
        return;
      }
    }
  }, [product, place.activityId, place.section, plan, products, isAdmin, adminKnown]);

  // The URL names the activity on screen. When it names none — or one this teacher can't see —
  // open the last one used this session, or the first, and say so in the URL rather than leave
  // a link that doesn't match the screen.
  useEffect(() => {
    if (!activity) return;
    rememberActivity(activity.activity_id);
    if (place.activityId === activity.activity_id) return;
    const next: Place = { ...place, activityId: activity.activity_id };
    wantedPath.current = pathFor(next);
    window.history.replaceState(null, "", wantedPath.current);
    setPlace(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity?.activity_id, place.activityId, product]);

  // tokens.css maps --accent to the product's colour from <html data-product>, so everything
  // that says "you are here" — including the page chrome outside .app — follows the product.
  useEffect(() => { document.documentElement.dataset.product = product; }, [product]);

  // A tab change within Writing keeps the activity; a product switch drops it.
  function goSection(s: Section) {
    go(product === "writing" && activity
      ? { product, section: s, activityId: activity.activity_id }
      : { product, section: s });
  }

  function goActivity(a: Activity) {
    go({ product: "writing", section, activityId: a.activity_id });
  }

  function go(next: Place) {
    wantedPath.current = pathFor(next);
    if (window.location.pathname !== wantedPath.current) {
      window.history.pushState(null, "", wantedPath.current);
    }
    rememberProduct(next.product);
    setPlace(next);
  }

  // Every selection resolves to a scoped backend query — no "fetch everything, filter
  // client-side". See ../CLAUDE.md: the browser never receives data outside the current scope.
  useEffect(() => {
    setSchools([]);
    setSel(null);
    setLoadState("loading");
    api
      .get<{ schools: SchoolRow[] }>(
        `/marts/attendance-diagnostic?district_id=${districtId}&level=${level}`,
      )
      .then((d) => {
        const rows = d.schools || [];
        setSchools(rows);
        // A session activation / chat set_school that crossed rosters parked its target here.
        const pending = pendingSelRef.current;
        pendingSelRef.current = null;
        setSel((pending && rows.find((s) => s.school_id === pending)) || rows[0] || null);
        setLoadState("ready");
      })
      .catch(() => setLoadState("error"));
  }, [level, districtId]);

  const current = useMemo(
    () => schools.find((s) => sel && s.school_id === sel.school_id) || sel,
    [schools, sel],
  );
  const currentId = current?.school_id;

  // Reconcile the active session with the selected school. Runs BEFORE the fetch effect
  // below (definition order), so an adopted spec is what gets fetched. The decision (adopt /
  // repoint-empty / create / fork, and dropping orphaned empty scratch sessions) lives in the
  // pure reconcileSchoolChange — see sessions.ts for the rules.
  useEffect(() => {
    if (!current) return;
    const cur = sessionsRef.current;
    const act = cur.find((s) => s.id === activeRef.current) ?? null;
    if (act && act.school_id === current.school_id) return;
    const fork = forkFromRef.current;
    forkFromRef.current = null;
    const r = reconcileSchoolChange(cur, activeRef.current, {
      school_id: current.school_id, school_name: current.school_name,
      district_id: districtId, level,
    }, fork, resolveDefault(level));
    setSessions(r.sessions);
    sessionsRef.current = r.sessions;
    setActiveId(r.activeId);
    activeRef.current = r.activeId;
    setWspec(r.spec);
    wspecRef.current = r.spec; // sibling fetch effect runs in this same commit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId]);

  function fetchWorkspace(schoolId: string, spec: WorkspaceSpec) {
    setWs(null);
    // One call restores every panel for the spec (charts + spotlight + plan).
    api
      .post<WorkspaceData>("/marts/workspace", { school_id: schoolId, spec })
      .then(setWs)
      .catch(() => setWs(null));
  }

  useEffect(() => {
    if (!currentId) {
      setPeers(null);
      setWs(null);
      return;
    }
    setPeers(null);
    api
      .get<{ peers: Peer[] }>(`/marts/like-schools?school_id=${currentId}&k=50`)
      .then((d) => setPeers(d.peers || []))
      .catch(() => setPeers([]));
    // Keyed on the school only: chat slot changes arrive WITH their payloads
    // (applyChatWorkspace), so a spec change alone never costs a refetch.
    fetchWorkspace(currentId, wspecRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId]);

  // The active session's spec follows wspec (chat mutations, adoption). Same-reference
  // specs (just adopted) short-circuit, so adoption doesn't echo a write.
  useEffect(() => {
    const id = activeRef.current;
    if (!id) return;
    setSessions((prev) => {
      const s = prev.find((x) => x.id === id);
      if (!s || s.workspace === wspec) return prev;
      return upsert(prev, { ...s, workspace: wspec, updated_at: Date.now() }, id);
    });
  }, [wspec]);

  // Chat transcript lands in the session the TURN belongs to — `sid` from the Chat that ran
  // it, NOT whatever is active now. This is what stops an answer arriving after the user has
  // switched sessions from landing in the wrong one. (title refines off the first question,
  // unless Claude renamed it — a custom title is never clobbered by the derivation.)
  function onMessages(sid: string | null, turns: ChatTurn[]) {
    const id = sid ?? activeRef.current;
    if (!id) return;
    setSessions((prev) => {
      const s = prev.find((x) => x.id === id);
      if (!s) return prev;
      const upd = { ...s, messages: turns, updated_at: Date.now() };
      if (!upd.custom_title) upd.title = titleFor(upd);
      return upsert(prev, upd, id);
    });
  }

  // Put a session on screen: adopt its school, spec, and transcript. Does NOT bump updated_at —
  // viewing a session is not activity, so the rail order stays put (a bump here is what made
  // selecting a session jump it to the top and read as a double-highlight).
  function adoptSession(s: Session) {
    setActiveId(s.id);
    activeRef.current = s.id;
    setWspec(s.workspace);
    wspecRef.current = s.workspace;
    if (s.district_id !== districtId || s.level !== level) {
      pendingSelRef.current = s.school_id;
      if (s.district_id !== districtId) setDistrictId(s.district_id);
      if (s.level !== level) setLevel(s.level);
    } else if (s.school_id !== currentId) {
      const row = schools.find((r) => r.school_id === s.school_id);
      if (row) setSel(row);
      else pendingSelRef.current = s.school_id;
    } else {
      // Same school already selected — no selection change to trigger a fetch, so refetch here.
      fetchWorkspace(s.school_id, s.workspace);
    }
  }

  // Rail click.
  function activateSession(s: Session) {
    if (s.id === activeId) return;
    adoptSession(s);
  }

  // Rail ✕: drop a session. Deleting the active one moves to the most recent remaining, or —
  // if none are left — starts a fresh session for the school currently on screen.
  function deleteSession(id: string) {
    const remaining = byRecency(sessionsRef.current.filter((s) => s.id !== id));
    setSessions(remaining);
    sessionsRef.current = remaining;
    if (id !== activeRef.current) return; // deleted a background session — screen unaffected
    const next = remaining[0];
    if (next) {
      adoptSession(next);
    } else if (current) {
      const fresh = createSession({
        school_id: current.school_id, school_name: current.school_name,
        district_id: districtId, level,
      }, resolveDefault(level));
      setSessions([fresh]);
      sessionsRef.current = [fresh];
      adoptSession(fresh);
    } else {
      setActiveId(null);
      activeRef.current = null;
      setWs(null);
    }
  }

  // Fresh look at the current school: old chat context must not contaminate a new inquiry.
  function newSession() {
    if (!current) return;
    const s = createSession({
      school_id: current.school_id, school_name: current.school_name,
      district_id: districtId, level,
    }, resolveDefault(level));
    setSessions((prev) => upsert(prev, s, s.id));
    setActiveId(s.id);
    setWspec(s.workspace);
    wspecRef.current = s.workspace;
    fetchWorkspace(current.school_id, s.workspace);
  }

  // Apply a chat turn's workspace mutations. `sid` is the session the turn belongs to. If the
  // user has since switched away, we still PERSIST the turn's spec/title to its own session,
  // but we do NOT disturb the screen (no slot changes, no school switch) — those belong to the
  // conversation that is no longer on-screen. Only when the turn's session is still active do
  // the live-screen effects run.
  function onWorkspace(sid: string | null, w: ChatWorkspace) {
    const id = sid ?? activeRef.current;
    if (id && id !== activeRef.current) {
      setSessions((prev) => {
        const s = prev.find((x) => x.id === id);
        if (!s) return prev;
        let upd: Session = { ...s, updated_at: Date.now() };
        if (w.session_title) upd = { ...upd, title: w.session_title, custom_title: true };
        // Only a same-school slot/spotlight spec belongs to THIS session. A set_school turn's
        // spec is the OTHER school's default — dropping it here avoids corrupting this session;
        // the school switch itself is intentionally not applied to a backgrounded conversation.
        if (w.spec && !w.school) upd = { ...upd, workspace: w.spec };
        return upsert(prev, upd, id);
      });
      return;
    }
    if (w.session_title) {
      // rename_session: retitle the session the turn ran in (before any fork below).
      const title = w.session_title;
      const id = activeRef.current;
      setSessions((prev) => {
        const s = prev.find((x) => x.id === id);
        if (!s) return prev;
        return upsert(prev, { ...s, title, custom_title: true, updated_at: Date.now() }, id);
      });
    }
    if (w.spec) setWspec(w.spec);
    if (w.school) {
      if (w.school.school_id === currentId) {
        // set_school to the school already on screen = a fresh look: fork the session and
        // apply its default payloads here (no selection change fires to do it for us).
        const src = sessionsRef.current.find((s) => s.id === activeRef.current) ?? null;
        const forked = forkSession(src, {
          school_id: w.school.school_id, school_name: w.school.school_name,
          district_id: w.school.district_id, level,
        }, resolveDefault(level));
        if (w.spec) forked.workspace = w.spec; // set_school sends the level-aware default
        setSessions((prev) => upsert(prev, forked, forked.id));
        setActiveId(forked.id);
        setWs((prev) =>
          prev
            ? {
                ...prev,
                spec: w.spec ?? prev.spec,
                slots: [0, 1, 2].map((i) => w.payloads[`slot_${i + 1}`] ?? prev.slots[i]),
                subgroup_slots: [null, null, null],
                spotlight: null,
              }
            : prev,
        );
      } else {
        forkFromRef.current = sessionsRef.current.find((s) => s.id === activeRef.current) ?? null;
        if (w.school.district_id !== districtId) {
          pendingSelRef.current = w.school.school_id;
          setDistrictId(w.school.district_id); // roster reload selects it on arrival
        } else {
          const row = schools.find((s) => s.school_id === w.school!.school_id);
          if (row) setSel(row);
          else pendingSelRef.current = w.school.school_id;
        }
      }
      return;
    }
    setWs((prev) => applyChatWorkspace(prev, w));
  }

  // What the main region shows instead of the screen, when something decides for the whole page.
  // In order: a refused sign-in for all of Student Learning, then loading, then a link to
  // something this teacher can't see, then an admin tab opened without admin tools.
  const asked = requestedTab(wantedPath.current);
  const adminBlocked = adminKnown && !isAdmin && !!asked && asked.product === product
    && !!tabFor(asked.product, asked.section)?.adminOnly;
  const gate = product === "writing" && activitiesError === 403 ? activitiesError : null;
  const override =
    gate === 403 ? <RefusedState detail={refusedDetail} />
    : product === "writing" && activities === null ? <PageSkeleton />
    : askedMissing ? (
        <NotFoundState what="activity" action={activities && activities.length > 0 && (
          <button className="btn primary" onClick={() => goActivity(chooseActivity(activities, undefined)!)}>
            Open your activities
          </button>)} />)
    : adminBlocked ? <AdminOnlyState />
    : null;

  // HERE RATHER THAN IN AuthGate. It sat inside the gate, which is where sign-in belongs and is
  // also where `dev/preview.html` does not go: that harness renders <App/> directly so every
  // screen can be looked at without a token, and a provider above it would have made the tooltips
  // invisible in the one tool built for checking screens. App only renders when signed in, so the
  // fetch is still behind the gate either way.
  return (
    <GlossaryProvider>
    {/* Keyed to the folder on screen. The Score control and the people list are in different
        trees and need the same answer, and polling it twice would ask Cloud Run twice. */}
    <ScoreRunProvider activityId={activity?.activity_id ?? null}>
    <Shell product={product} products={products} isAdmin={isAdmin}
           district={product === "writing" ? district : null}
           tabs={navTabsFor(product, isAdmin, activity?.scored ?? true)} section={section}
           onProduct={(p) => go({ product: p, section: sectionsFor(p, isAdmin)[0] })}
           onSection={goSection}
           bare={gate !== null}
           currentSection={adminBlocked && asked ? asked.section : section}
           activitiesFailed={activitiesError !== null && gate === null}
           activities={activities} activitiesAvailable={activitiesAvailable}
           activity={activity} onActivity={goActivity}
           courses={product === "writing" ? plan?.courses ?? null : null}
           activeHref={navHref}
           needs={product === "writing" ? plan?.needs ?? [] : []}
           onOpenLesson={(href) => { window.history.pushState(null, "", href);
                                     setPlace(placeFromPath(href, products, isAdmin, product)); }}
           onPlanChanged={refreshPlan}
           years={product === "writing" ? plan?.years ?? [] : []}
           year={plan?.school_year ?? null}
           onYear={(y) => setYear(y)}
           strip={product === "writing" ? (
             <>
               {/* NOT ABOVE AN OPEN FOLDER. This strip spans every cohort on purpose — it is for
                   deciding what to do next, met on the way to the work. Once a folder IS open
                   that decision is made, and a row about another cohort reads as something wrong
                   with THIS one: "nobody is named on this paper" over a folder where everybody
                   is. Its own note already says the folder carries a count in the tree, which is
                   where a teacher goes looking; this was the same news, shouted in the wrong
                   room. */}
               {plan && plan.available && !(section === "review" && activity) && (
                 <NeedsStrip needs={plan.needs}
                             onOpen={(href) => { window.history.pushState(null, "", href);
                                                 setPlace(placeFromPath(href, products, isAdmin, product)); }} />
               )}
               {/* The COLLECTION's actions, and only when one is open. Checking for new work is
                   a question about one folder; on a lesson or unit page there is no folder it
                   could mean, and the bar offered to add one to nothing in particular. */}
               {section === "review" && activity && <div className="lessonbar">
                 <Incoming folder={activity ? {
                             activity_id: activity.activity_id,
                             url: folderOf(plan, activity.activity_id)?.folder_url ?? null,
                             name: folderOf(plan, activity.activity_id)?.folder_name ?? null,
                           } : null}
                           onFolderSaved={(f) => {
                             // What the server CONFIRMED, and nothing else. Refetching the whole
                             // plan to learn one field we were just told is a second request that
                             // can be slow, fail, or — as it did in the preview — answer with the
                             // old value and put "No folder yet" back over a folder the teacher
                             // had just set, which reads as the save having silently failed.
                             if (activity) setPlan((p) => withFolder(p, activity.activity_id, f));
                           }}
                           onTaken={() => { setReload((n) => n + 1); refreshPlan(); }} />
                 {/* WHERE THE CONNECTION IS FIXED IS WHERE IT BREAKS. "Check for new student
                     work" is the button that needs a Drive, so the control that connects one
                     sits beside it rather than behind a settings page nobody opens. */}
                 <DriveConnection onChanged={() => setReload((n) => n + 1)} />
                 {/* AND THEN SCORE IT. Beside the button that brings work in, because that is
                     the order a teacher does them in and the second is useless without the
                     first. */}
                 <ScoreRun activityId={activity?.activity_id ?? null} />
               </div>}
             </>
           ) : null}>
      {override ?? (section === "workspace" ? (
        <div className="cols">
          <div className="rail">
            <button className="new-sess" onClick={newSession} disabled={!current}>
              + New session
            </button>
            <div className="rail-scroll">
              <div className="rail-list">
                {byRecency(sessions).map((s) => (
                  <div
                    key={s.id}
                    className={"sess" + (s.id === activeId ? " on" : "")}
                    onClick={() => activateSession(s)}
                    title={s.title}
                  >
                    <button
                      className="sess-del"
                      title="Delete this session"
                      onClick={(e) => {
                        e.stopPropagation(); // don't also activate the row we're deleting
                        deleteSession(s.id);
                      }}
                    >
                      ×
                    </button>
                    <div className="sess-title">{s.title}</div>
                    <div className="sess-meta">
                      {s.school_name} · {relTime(s.updated_at)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

      <div className="diag">
        <div className="school-hd">
          <select
            className="pill pill-lg"
            title="Select a school"
            value={current ? current.school_id : ""}
            onChange={(e) => setSel(schools.find((s) => s.school_id === e.target.value) || null)}
          >
            {schools.length === 0 ? (
              <option>
                {loadState === "loading"
                  ? "loading…"
                  : loadState === "error"
                    ? "failed to load"
                    : "no schools"}
              </option>
            ) : (
              schools.map((s) => (
                <option key={s.school_id} value={s.school_id}>
                  {s.alignment === "unmet_need" ? "⚠ " : ""}
                  {s.school_name}
                </option>
              ))
            )}
          </select>
          <div className="school-sub">
            <select
              className="pill"
              title="Select a district"
              value={districtId}
              onChange={(e) => setDistrictId(e.target.value)}
            >
              {districts.length === 0 ? (
                <option value={districtId}>Long Beach Unified</option>
              ) : (
                districts.map((d) => (
                  <option key={d.district_id} value={d.district_id}>
                    {d.district_name}
                  </option>
                ))
              )}
            </select>
            <select
              className="pill"
              title="Select a level"
              value={level}
              onChange={(e) => setLevel(e.target.value as Level)}
            >
              {LEVELS.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
            <span className="sub-x">schools</span>
          </div>
          {current ? (
            <div className="feats">
              <Feat v={current.enroll_total} kind="num" lab="enrollment" />
              <Feat v={current.pct_sed} kind="pct" lab="econ-disadv" />
              <Feat v={current.pct_el} kind="pct" lab="English learners" />
              <Feat v={current.pct_swd} kind="pct" lab="students w/ disab." />
              <Feat v={current.locale} kind="str" lab="locale" />
            </div>
          ) : null}
        </div>
        <Diagnostic s={current} peers={peers} ws={ws} />
      </div>
      <div className="chat">
        {mayChat ? (
          <Chat
            key={activeId ?? "boot"}
            school={current}
            level={level}
            wspec={wspec}
            onWorkspace={onWorkspace}
            sessionId={activeId}
            initialMessages={activeSession?.messages ?? []}
            onMessages={onMessages}
          />
        ) : mayChat === false ? <NoChatState /> : null}
      </div>
        </div>
      ) : section === "plan" ? (
        <PlanView plan={plan} kind={place.planKind ?? "unit"} id={place.planId ?? ""}
                  onOpen={goTo} onCreated={refreshPlan} />
      ) : section === "glossary" ? (
        <GlossaryPage />
      ) : section === "access" ? (
        <Access />
      ) : section === "people" ? (
        <People onOpenAccess={(s) => { go({ product: "admin", section: "access" });
                                       writeQuery("person", s, { replace: true }); }} />
      ) : section === "districts" ? (
        <Districts />
      ) : section === "schools" ? (
        <SchoolsScreen />
      ) : section === "content" ? (
        <ContentProviders />
      ) : section === "platform" ? (
        <PlatformScreen />
      ) : section === "roles" ? (
        <Roles />
      ) : section === "curricula" ? (
        <ContentCurricula />
      ) : section === "tasks" ? (
        <ContentTasks />
      ) : section === "traits" ? (
        <ContentTraits />
      ) : section === "rubrics" && product === "content" ? (
        <ContentRubrics />
      ) : section === "rubrics" ? (
        <ScoringRubrics />
      ) : section === "raters" ? (
        <ScoringRaters />
      ) : section === "fit-gates" ? (
        <ScoringFitGates />
      ) : section === "scorers" ? (
        <ScoringScorers />
      ) : section === "models" ? (
        <ScoringModels />
      ) : section === "sets" ? (
        <ScoringSets />
      ) : section === "experiments" ? (
        <ScoringExperiments />
      ) : section === "folders" ? (
        <FolderConfirm scope={activity ? scopeOf(activity) : null} />
      ) : section === "review" ? (
        // The title names the lesson AND the collection. Scores belong to one collection of
        // student work — a draft and a final are different sets of papers — so a console headed
        // only "Culminating op-ed" claims the scores are the lesson's, which no lesson has.
        <ReviewConsole key={`${activity?.activity_id ?? "all"}:${reload}`}
                       activityId={activity?.activity_id ?? null}
                       scope={activity ? scopeOf(activity) : null}
                       // THE CLASS BELONGS HERE. One lesson is taught to several sections and
                       // each gets its own folder, so "Final literary analysis · Ray Run Scored
                       // Work" names the lesson and the folder and never says whose class the
                       // papers are. `section_label` has been on the activity all along.
                       scopeTitle={activity
                         ? [activity.lesson_title, activity.section_label,
                            folderLabel(activity.iteration)].filter(Boolean).join(" · ")
                         : ""} />
      ) : (
        <EvalWorkbench section={section} />
      ))}
    </Shell>
    </ScoreRunProvider>
    </GlossaryProvider>
  );
}
